<template>
  <ReportCellWrapper
    :trial-over="params.data?.trialOver"
    :is-disabled-by-tier="params.data?.isDisabledByTier"
    class="max-w-full"
  >
    <div class="flex flex-col max-w-full">
      <span
        v-if="type === COL_TYPES.LAST_EDITED"
        class="reports-user-cell__edited-text"
      >
        {{ getRelativeEditTime }}
      </span>
      <div v-if="user" class="flex flex-row items-center gap-4 max-w-full">
        <FontAwesomeIcon
          :icon="['fak', 'person']"
          :class="{
            'h-12 w-12': type === COL_TYPES.LAST_EDITED,
            'h-14 w-14': type === COL_TYPES.OWNER,
          }"
          class="text-neutral-accent"
        />
        <span
          class="reports-user-cell__user-text min-w-0"
          :class="{
            'text-neutral-text-weak text-sm': type === COL_TYPES.LAST_EDITED,
            'text-neutral-text-strong': type === COL_TYPES.OWNER,
          }"
        >
          {{ displayName }}
        </span>
      </div>
      <div v-else-if="type === COL_TYPES.OWNER"> -- </div>
    </div>
  </ReportCellWrapper>
</template>

<script setup lang="ts">
import { ICellRendererParams } from '@ag-grid-community/core';
import { DateTime } from 'luxon';
import { computed } from 'vue';

import ReportCellWrapper from '@/components/reports/ReportCellWrapper.vue';

import { ReportCell } from '@/types/reports';

const { params } = defineProps<{
  params: ICellRendererParams<ReportCell>;
}>();

const COL_TYPES = {
  OWNER: 'owner',
  LAST_EDITED: 'lastEdited',
} as const;
type ColTypes = (typeof COL_TYPES)[keyof typeof COL_TYPES];
const type = computed(() => params.colDef?.field as ColTypes);
const user = computed(() =>
  type.value === COL_TYPES.LAST_EDITED
    ? params.data?.updatedByUser
    : params.data?.createdByUser,
);

const displayName = computed(() => {
  if (!user.value) return '';
  const { first_name, last_name, email } = user.value;
  if (first_name || last_name) {
    return [first_name, last_name].filter(Boolean).join(' ');
  }
  return email ?? '';
});

const getRelativeEditTime = computed(() =>
  DateTime.fromISO(params.data?.report?.updated_at || '').toFormat('DDD'),
);
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.reports-user-cell__edited-text {
  @apply text-neutral-800 text-base mb-4;
}

.reports-user-cell__user-text {
  @apply text-base text-neutral-500 truncate;
}
</style>
